import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { naprawWiersze, podgladNaprawy } from '@/lib/planogram-api';

/**
 * Odmiana „wiersz" przez liczbę — 1 / 2-4 / reszta, z wyjątkiem nastek (12, 13, 14 idą jak reszta).
 * Bez tego pasek mówił „1 wierszy", a to jest pierwsze zdanie, jakie operator czyta o awarii.
 */
const wierszy = (n: number): string => {
  const dziesiatki = n % 100;
  if (n === 1) return 'wiersz';
  if (dziesiatki >= 12 && dziesiatki <= 14) return 'wierszy';
  return n % 10 >= 2 && n % 10 <= 4 ? 'wiersze' : 'wierszy';
};

/**
 * PASEK NAPRAWY WIERSZY ZAŁOŻONYCH PRZEZ PORTAL NIEPEŁNIE.
 *
 * ⚠️ TO NIE JEST FUNKCJA PLANOGRAMU, tylko jedyne miejsce, w którym widać skutek naszego błędu.
 * Zanim znaliśmy komplet kolumn wymaganych przez SPS, `INSERT` zostawiał ich część pustych —
 * a program po takim wierszu przestaje OTWIERAĆ BĘBEN automatu. Portal pokazuje maszynę dalej
 * normalnie, bo jego czytnik jest luźniejszy, więc bez tego paska awaria jest niewidoczna
 * z portalu i wychodzi dopiero przy magazynierze stojącym przy automacie.
 *
 * ⚠️ PASEK ZNIKA SAM. Naprawiony wiersz przestaje się kwalifikować (portal poznaje to po
 * kształcie zapisanego ładunku, nie po dacie), więc licznik schodzi do zera bez sprzątania ręką.
 */
export function PasekNaprawy({ groupId, machineId }: { groupId: string; machineId: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [pyta, setPyta] = useState(false);

  const podglad = useQuery({
    queryKey: ['planogram-naprawa', groupId, machineId],
    queryFn: () => podgladNaprawy(groupId, machineId),
  });

  const napraw = useMutation({
    mutationFn: () => naprawWiersze(groupId, machineId),
    onSuccess: async (r) => {
      setPyta(false);
      toast.success(
        `Zakolejkowano naprawę: ${r.naprawionych} ${wierszy(r.naprawionych)} — agent wykona ją przy najbliższym cyklu`,
      );
      await qc.invalidateQueries({ queryKey: ['planogram-naprawa'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const ile = podglad.data?.doNaprawy ?? 0;
  const pominiete = podglad.data?.pominiete ?? [];
  if (ile === 0 && pominiete.length === 0) return null;

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
        <TriangleAlert className="size-4 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1 space-y-1">
          {ile > 0 && (
            <p>
              <strong>{ile}</strong> {wierszy(ile)} tego automatu portal założył bez kolumn, których SPS nigdy
              nie zostawia pustych. Program może przez to nie otwierać bębna.
            </p>
          )}
          {pominiete.length > 0 && (
            <p className="text-muted-foreground">
              Poza naprawą: {pominiete.join('; ')}. Odtworzenie takiego wiersza skasowałoby stan — najpierw
              trzeba go wybrać z automatu.
            </p>
          )}
        </div>
        {ile > 0 && (
          <Button size="sm" variant="outline" onClick={() => setPyta(true)}>
            Napraw
          </Button>
        )}
      </div>

      <Dialog open={pyta} onOpenChange={setPyta}>
        <DialogContent
          title={`Uzupełnić kolumny w ${ile === 1 ? 'jednym wierszu' : `${ile} wierszach`}?`}
          description="Portal odtworzy je w bazie automatu w takiej postaci, w jakiej zapisuje je sam SPS."
        >
          <div className="space-y-3 text-sm">
            <p>Limity — maksimum, minimum i próg ponownego zamówienia — zostają zachowane.</p>
            <p className="text-muted-foreground">
              Wracają na zero „szybkie uzupełnianie" i „wydawanie swobodne": tych dwóch ustawień portal
              u siebie nie trzyma, więc nie ma ich skąd odtworzyć. Na wierszach, o których tu mowa, i tak
              są zerowe.
            </p>
            <p className="text-muted-foreground">
              Zmiana idzie zwykłą drogą — kolejka zmian i agent, ze śladem w rejestrze operacji.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPyta(false)} disabled={napraw.isPending}>
              Anuluj
            </Button>
            <Button onClick={() => napraw.mutate()} disabled={napraw.isPending}>
              {napraw.isPending && <Loader2 className="animate-spin" />}
              Napraw
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
